import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Lock, Mail, ArrowRight } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function LoginPage() {
  const { user, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("admin@norea.pro");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(location.state?.error || "");

  if (user) return <Navigate to="/" replace />;

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      window.location.href = "/";
    } catch (err) {
      setError(err?.response?.data?.detail || "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - form */}
      <div className="w-full md:w-1/2 flex items-center justify-center px-8 bg-norea-bg">
        <div className="w-full max-w-md">
          <div className="mb-12" data-testid="login-logo">
            <img
              src="/norea-logo-hero.png"
              alt="Norea"
              className="h-16 w-auto select-none"
              draggable={false}
            />
            <div className="overline mt-2">AI Platform</div>
          </div>

          <h1 className="font-heading text-3xl font-semibold text-norea-text mb-2">
            Pilotez vos agents IA
          </h1>
          <p className="text-sm text-norea-textMuted mb-8">
            Accédez à votre console d'administration pour superviser le traitement automatisé des dossiers de financement.
          </p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="overline block mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-norea-textDisabled" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field pl-10"
                  placeholder="admin@norea.pro"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <label className="overline block mb-1.5">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-norea-textDisabled" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pl-10"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            {error && (
              <div className="text-sm text-status-erreurText bg-status-erreurBg border border-status-erreurBorder px-3 py-2 rounded-md" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="login-submit-button"
            >
              {loading ? "Connexion…" : "Se connecter"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5 text-xs text-norea-textMuted"><div className="flex-1 h-px bg-norea-border" />ou<div className="flex-1 h-px bg-norea-border" /></div>
          <button type="button" onClick={googleLogin} className="btn-secondary w-full" data-testid="login-google-button">
            <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
            Se connecter avec Google
          </button>
          <p className="text-[11px] text-norea-textMuted mt-2">Réservé aux comptes autorisés par l'administrateur.</p>

          <div className="mt-8 text-xs text-norea-textMuted">
            Identifiants par défaut : <code className="bg-norea-surfaceHover px-1.5 py-0.5 rounded">admin@norea.pro</code> / <code className="bg-norea-surfaceHover px-1.5 py-0.5 rounded">NoreaAdmin2026!</code>
          </div>
        </div>
      </div>

      {/* Right - brand panel with Norea colors */}
      <div
        className="hidden md:flex md:w-1/2 relative items-center justify-center overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #1a3a2e 0%, #2d6a5a 35%, #4898c8 100%)",
        }}
      >
        {/* Decorative arc echoing the logo */}
        <div
          className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl"
          style={{
            background: "radial-gradient(circle, #98c068 0%, transparent 60%)",
          }}
        />
        <div
          className="absolute -bottom-40 -left-20 w-[500px] h-[500px] rounded-full opacity-15 blur-3xl"
          style={{
            background: "radial-gradient(circle, #4898c8 0%, transparent 70%)",
          }}
        />

        <div className="relative z-10 px-12 py-16 max-w-lg text-center">
          <img
            src="/norea-logo-hero.png"
            alt="Norea"
            className="h-44 w-auto mx-auto mb-6 select-none drop-shadow-2xl"
            draggable={false}
            data-testid="login-hero-logo"
          />
          <p
            className="italic text-lg mb-10"
            style={{ color: "#a8d4e8", letterSpacing: "0.01em" }}
            data-testid="login-tagline"
          >
            Une énergie saine, pour une vie saine.
          </p>
          <div className="pt-8 border-t border-white/10">
            <div className="overline text-white/60 mb-2">Plateforme IA · Traitement BDC</div>
            <div className="font-heading text-2xl font-medium leading-snug text-white">
              Automatiser le traitement des dossiers de financement énergétique.
            </div>
            <div className="mt-4 text-sm text-white/70">
              Photovoltaïque · Pompes à chaleur · Isolation thermique
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
